import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Timeline as TimelineIcon, Groups as GroupsIcon } from '@mui/icons-material';

// ユーティリティ関数 - hasData を除外するように修正
const cleanProps = ({
  tooltipPayload,
  tooltipPosition,
  dataKey,
  payload,
  hasData,          // ← これを追加して除外
  ...rest
}) => rest;

const AnalysisChart = ({
  analysisData,
  chartType,
  selectedClient,
  isCurrentMonthSelected,
  selectedDateRange, // 🔧 追加：フロントエンドから実際の選択期間を受け取る
  colors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#ff0000', 
    '#00ff00', '#0000ff', '#ff00ff', '#00ffff', '#ffff00'
  ]
}) => {
  // 🔧 修正：選択期間が1ヶ月かどうかで判定（データ存在数ではなく）
  const isSingleMonthData = () => {
    if (!selectedDateRange) return false;
    
    try {
      const startDate = new Date(selectedDateRange.startDate);
      const endDate = new Date(selectedDateRange.endDate);
      
      // 開始月と終了月が同じかどうかで判定
      return startDate.getFullYear() === endDate.getFullYear() && 
             startDate.getMonth() === endDate.getMonth();
    } catch (error) {
      // フォールバック：データ存在数で判定
      return analysisData?.monthlyData?.length === 1;
    }
  };

  // 🔧 新規：月平均データが利用可能かチェック
  const hasValidAverageData = () => {
    if (!analysisData?.allTimeMonthlyAverage) return false;
    if (analysisData.allTimeMonthlyAverage <= 0) return false;
    
    // dataStartPeriod にデータ不足の文言が含まれていないかチェック
    const description = analysisData.dataStartPeriod || '';
    return !description.includes('データ不足') && !description.includes('データなし');
  };

  // 🔧 修正：期間内の全ての月を生成（フロントエンド期間を使用）
  const generateAllMonthsInRange = () => {
    // フロントエンドの選択期間を使用（データの存在に関係なく）
    if (!selectedDateRange) return [];
    
    try {
      const startDate = new Date(selectedDateRange.startDate);
      const endDate = new Date(selectedDateRange.endDate);
      
      const months = [];
      const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      
      while (current <= end) {
        const yearMonth = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        // 短縮表示：2024年8月 → 24年8月
        const shortYear = String(current.getFullYear()).slice(-2);
        const displayMonth = `${shortYear}年${current.getMonth() + 1}月`;
        months.push({ yearMonth, displayMonth });
        current.setMonth(current.getMonth() + 1);
      }
      
      return months;
    } catch (error) {
      console.error('期間解析エラー:', error);
      // 🔧 フォールバック：analysisData.period を使用
      return generateAllMonthsFromPeriod();
    }
  };

  // 🔧 新規：フォールバック用の期間生成（元のロジック）
  const generateAllMonthsFromPeriod = () => {
    if (!analysisData?.period) return [];
    
    try {
      const [startDateStr, endDateStr] = analysisData.period.split(' 〜 ');
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      
      const months = [];
      const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      
      while (current <= end) {
        const yearMonth = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        const shortYear = String(current.getFullYear()).slice(-2);
        const displayMonth = `${shortYear}年${current.getMonth() + 1}月`;
        months.push({ yearMonth, displayMonth });
        current.setMonth(current.getMonth() + 1);
      }
      
      return months;
    } catch (error) {
      console.error('期間解析エラー:', error);
      return [];
    }
  };

  // チャートデータ変換（全ての月を含む）
  const getChartData = () => {
    if (!analysisData?.monthlyData) return [];
    
    const allMonths = generateAllMonthsInRange();
    const dataMap = new Map();
    
    // 実際のデータをマップに格納
    analysisData.monthlyData.forEach(month => {
      if (month && month.month) {
        dataMap.set(month.month, {
          month: month.displayMonth || month.month,
          amount: month.totalAmount || 0,
          'data-is-current': month.isCurrentMonth || false,
          hasData: true
        });
      }
    });
    
    // 全ての月でデータを生成（データがない月は0）
    return allMonths.map(({ yearMonth, displayMonth }) => {
      if (dataMap.has(yearMonth)) {
        return dataMap.get(yearMonth);
      } else {
        // データがない月
        const currentMonthKey = new Date().toISOString().slice(0, 7); // YYYY-MM
        return {
          month: displayMonth,
          amount: 0,
          'data-is-current': yearMonth === currentMonthKey && isCurrentMonthSelected,
          hasData: false
        };
      }
    });
  };

  // 顧客別内訳データ
  const getClientBreakdownData = () => {
    if (!analysisData?.monthlyData || selectedClient) return [];
    
    const clientTotals = {};
    analysisData.monthlyData.forEach(month => {
      month.clientBreakdown?.forEach(client => {
        if (!clientTotals[client.clientName]) {
          clientTotals[client.clientName] = 0;
        }
        clientTotals[client.clientName] += client.amount;
      });
    });
    
    return Object.entries(clientTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  };

  // カスタムBarコンポーネント
  const CustomBar = (props) => {
    const { payload } = props;
    const cleanedProps = cleanProps(props);
    
    // データがない場合の色分け
    if (!payload?.hasData) {
      return <rect {...cleanedProps} fill="#e0e0e0" opacity={0.5} />;
    }
    
    const fill = payload?.['data-is-current'] ? '#ff7300' : '#667eea';
    return <rect {...cleanedProps} fill={fill} />;
  };

  // カスタムDotコンポーネント
  const CustomDot = (props) => {
    const { payload, cx, cy } = props;
    const cleanedProps = cleanProps(props);
    
    // データがない場合
    if (!payload?.hasData) {
      return (
        <circle
          {...cleanedProps}
          cx={cx}
          cy={cy}
          r={4}
          fill="#e0e0e0"
          opacity={0.5}
        />
      );
    }
    
    const isCurrentMonth = payload?.['data-is-current'];
    return (
      <circle
        {...cleanedProps}
        cx={cx}
        cy={cy}
        r={isCurrentMonth ? 8 : 6}
        fill={isCurrentMonth ? '#ff7300' : '#667eea'}
      />
    );
  };

  // カスタムTooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = payload[0].value;
      
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>{label}</p>
          {data.hasData ? (
            <>
              <p style={{ margin: 0, color: '#666' }}>
                供給量: {value.toLocaleString()}{analysisData.productUnit || 'L'}
                {data['data-is-current'] ? ' (今月)' : ''}
              </p>
            </>
          ) : (
            <p style={{ margin: 0, color: '#999', fontStyle: 'italic' }}>
              供給実績なし
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // データなしの表示
  const NoDataDisplay = () => (
    <Box sx={{ 
      height: 400, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      color: 'text.secondary' 
    }}>
      <TimelineIcon sx={{ fontSize: 64, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        データがありません
      </Typography>
      <Typography variant="body2" textAlign="center">
        選択した製品・期間に供給データが存在しません<br />
        {selectedClient ? '顧客フィルターを解除するか、' : ''}期間を変更するか、データを登録してください
      </Typography>
    </Box>
  );

  // 🔧 修正：1ヶ月データの表示（平均データ有無で分岐）
  const SingleMonthComparisonChart = () => {
    if (!analysisData?.monthlyData || analysisData.monthlyData.length === 0) {
      return <NoDataDisplay />;
    }
    
    const monthData = analysisData.monthlyData[0];
    const currentAmount = monthData?.totalAmount || 0;
    const averageAmount = analysisData.allTimeMonthlyAverage || 0;
    const hasAverage = hasValidAverageData();

    // 短縮表示に対応
    const shortDisplayMonth = monthData?.displayMonth?.replace(/(\d{4})年/, (match, year) => {
      return String(year).slice(-2) + '年';
    }) || '不明な月';

    // 今月かどうかを判定
    const isCurrentMonth = monthData?.isCurrentMonth || false;

    // 🔧 修正：平均データがない場合は実績のみ表示
    const chartData = hasAverage ? [
      {
        name: isCurrentMonth ? '今月実績' : '対象月実績',
        amount: currentAmount,
        fill: '#1976d2'
      },
      {
        name: '月平均',
        amount: Math.round(averageAmount),
        fill: '#2e7d32'
      }
    ] : [
      {
        name: isCurrentMonth ? '今月実績' : '対象月実績',
        amount: currentAmount,
        fill: '#1976d2'
      }
    ];

    return (
      <Box sx={{ height: 500 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, textAlign: 'center' }}>
          {hasAverage ? (
            isCurrentMonth ? 
              `今月実績 vs 月平均` : 
              `${shortDisplayMonth} vs 月平均`
          ) : (
            isCurrentMonth ? 
              `今月実績` : 
              `${shortDisplayMonth} 実績`
          )}
          {isCurrentMonth && (
            <Chip 
              label="今月" 
              size="small" 
              color="primary" 
              sx={{ ml: 2 }}
            />
          )}
          {!hasAverage && (
            <Chip 
              label="平均データ不足" 
              size="small" 
              color="warning" 
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            maxBarSize={120}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              interval={0}
            />
            <YAxis />
            <RechartsTooltip
              formatter={(value) => [
                `${value.toLocaleString()}${analysisData.productUnit || 'L'}`, 
                '供給量'
              ]}
            />
            <Bar 
              dataKey="amount" 
              fill={(entry) => entry.fill}
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        {/* 🔧 修正：比較情報表示（平均データがある場合のみ） */}
        {hasAverage && averageAmount > 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            mt: 2,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              fontWeight: 500
            }}>
              平均比較: {((currentAmount / averageAmount - 1) * 100).toFixed(1)}%
            </Typography>
          </Box>
        )}

        {/* 🔧 新規：データ不足時の説明 */}
        {!hasAverage && (
          <Box sx={{ 
            textAlign: 'center', 
            mt: 2,
            p: 2,
            bgcolor: 'warning.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.300'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}>
              月平均比較にはより多くのデータが必要です
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // 複数月データの推移表示
  const MultiMonthTrendChart = () => (
    <Box sx={{ height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        {chartType === 'bar' ? (
          <BarChart 
            data={getChartData()}
            maxBarSize={60}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={65}
            />
            <YAxis />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="amount" 
              shape={<CustomBar />}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        ) : (
          <LineChart data={getChartData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={65}
            />
            <YAxis />
            <RechartsTooltip content={<CustomTooltip />} />
            <Line
              type="linear"
              dataKey="amount"
              stroke="#667eea"
              strokeWidth={3}
              dot={<CustomDot />}
              connectNulls={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </Box>
  );

  // 顧客別内訳パイチャート
  const ClientBreakdownChart = () => (
    <Box sx={{ height: 400 }}>
      {getClientBreakdownData().length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={getClientBreakdownData()}
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {getClientBreakdownData().map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <RechartsTooltip 
              formatter={(value) => `${value.toLocaleString()}${analysisData.productUnit || 'L'}`} 
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ 
          height: 400, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary' 
        }}>
          <GroupsIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            顧客データなし
          </Typography>
          <Typography variant="body2" textAlign="center">
            選択期間に顧客別データがありません
          </Typography>
        </Box>
      )}
    </Box>
  );

  // メインレンダリング
  if (!analysisData) {
    return <NoDataDisplay />;
  }

  // 期間内にデータが全くない場合でもグラフは表示
  const hasAnyData = analysisData.monthlyData && analysisData.monthlyData.length > 0;

  // チャートタイトル
  const getChartTitle = () => {
    if (isSingleMonthData()) {
      const hasAverage = hasValidAverageData();
      if (hasAverage) {
        return isCurrentMonthSelected ? '今月実績比較' : '月実績比較';
      } else {
        return isCurrentMonthSelected ? '今月実績' : '月実績';
      }
    }
    return (
      <>
        月別供給量推移
        {isCurrentMonthSelected && (
          <Chip 
            label="現在月含む" 
            size="small" 
            color="info" 
            sx={{ ml: 2 }}
          />
        )}
        {!hasAnyData && (
          <Chip 
            label="データなし期間" 
            size="small" 
            color="warning" 
            sx={{ ml: 2 }}
          />
        )}
      </>
    );
  };

  return {
    title: getChartTitle(),
    mainChart: isSingleMonthData() ? <SingleMonthComparisonChart /> : <MultiMonthTrendChart />,
    clientChart: <ClientBreakdownChart />,
    isSingleMonth: isSingleMonthData()
  };
};

export default AnalysisChart;